import * as t from 'io-ts';
import { optional } from './helper';

const golfPlayer = t.type({
  fullName: t.string,
  shortName: optional(t.string),
  displayName: optional(t.string),
  countryCode: optional(t.string),
  countryISO3: optional(t.string),
});

const golfRound = t.type({
  strokes: t.number,
  no: t.number,
  roundName: t.string,
  scoreToParFormatted: optional(t.string),
  holesPlayedFormatted: optional(t.string),
});

const golfStatus = t.type({
  id: t.number,
  name: t.string,
});

const tourObj = t.type({
  icon: optional(t.string),
  id: optional(t.string),
  name: optional(t.string),
});

export const golfStrokePlayerScore = t.type({
  currentRoundNumber: t.number,
  position: t.number,
  dateUpdated: t.string,
  isTie: t.boolean,
  missedCut: t.boolean,
  strokes: t.number,
  scoreToParFormatted: optional(t.string),
  points: t.number,
  roundsPlayed: t.number,
  player: golfPlayer,
  rounds: t.array(golfRound),
  thruFormatted: optional(t.string),
  todayFormatted: optional(t.string),
});

export const golfStrokeSummaryData = t.array(golfStrokePlayerScore);
export type GolfStrokeSummaryData = t.TypeOf<typeof golfStrokeSummaryData>;

export const golfStrokeSummary = t.type({
  currentRound: t.number,
  data: optional(golfStrokeSummaryData),
  eventDateEnd: t.string,
  eventDateStart: t.string,
  eventId: t.string,
  eventName: t.string,
  tour: tourObj,
  tournament: tourObj,
  tourId: t.string,
  venue: t.type({
    venueName: optional(t.string),
  }),
  status: golfStatus,
  feedId: t.string,
  subStatus: optional(golfStatus),
});

export type GolfStrokeSummaryApi = t.TypeOf<typeof golfStrokeSummary>;

const golfVenue = t.type({
  venueName: optional(t.string),
  venueShortName: optional(t.string),
  country: optional(
    t.type({
      fifaCode: optional(t.string),
      iso3Code: optional(t.string),
      name: optional(t.string),
      flag: optional(t.string),
    }),
  ),
});

const cupScores = t.type({
  team: t.type({
    teamId: t.string,
    name: t.string,
  }),
  score: t.string,
});

const roundHoles = t.type({
  winner: optional(t.string),
  no: t.number,
});

const teams = t.type({
  name: t.string,
  players: t.array(golfPlayer),
  teamId: t.string,
});

export const matches = t.type({
  drawType: t.string,
  holeThrough: t.number,
  holes: t.array(roundHoles),
  matchType: t.string,
  matchStatus: t.string,
  result: optional(
    t.type({
      description: optional(t.string),
      holesLeft: t.number,
      winner: t.string,
      size: t.number,
    }),
  ),
  roundNo: t.number,
  teams: t.array(teams),
  tee: t.string,
});
export type MatchPlayMatches = t.TypeOf<typeof matches>;

export const golfMatchPlaySummary = t.type({
  cupScores: optional(t.array(cupScores)),
  currentRound: t.number,
  match: optional(matches),
  eventDateEnd: t.string,
  eventDateStart: t.string,
  eventId: t.string,
  eventName: t.string,
  status: golfStatus,
  subStatus: optional(golfStatus),
  tournament: optional(tourObj),
  tour: tourObj,
  tournamentName: optional(t.string),
  awayTeamId: optional(t.string),
  homeTeamId: optional(t.string),
  resultDescription: optional(t.string),
  tourId: t.string,
  feedId: t.string,
  venue: golfVenue,
  isSingles: t.boolean,
});

export type GolfMatchPlaySummaryApi = t.TypeOf<typeof golfMatchPlaySummary>;

export const golfRanking = t.array(
  t.type({
    rank: t.number,
    eventsPlayed: t.number,
    player: golfPlayer,
    points: optional(t.number),
    money: optional(t.type({
      amount: t.number,
    })),
  }),
);
export type GolfRanking = t.TypeOf<typeof golfRanking>;

export type GolfRankingId = 'fedex-id' | 'order-of-merit';

export const golfLeaderboardRow = t.type({
  scoreToParFormatted: optional(t.string),
  thruFormatted: optional(t.string),
  todayFormatted: optional(t.string),
  strokes: optional(t.number),
  rounds: optional(t.array(golfRound)),
  player: t.type({
    displayName: t.string,
    countryISO3: t.string,
  }),
});

export type GolfLeaderboardRow = t.TypeOf<typeof golfLeaderboardRow>;

export const golfLeaderboard = t.type({
  leaderboard: optional(t.array(golfLeaderboardRow)),
  gameType: t.string,
});

export const matchRounds = t.type({
  roundName: t.string,
  matches: t.array(matches),
});
export type MatchRounds = t.TypeOf<typeof matchRounds>;

export const golfMatchPlayLeaderboard = t.type ({
  awayTeamId: optional(t.string),
  homeTeamId: optional(t.string),
  cupScores: optional(t.array(cupScores)),
  currentRound: t.number,
  matchRounds: t.array(matchRounds),
  eventDateEnd: t.string,
  eventDateStart: t.string,
  eventId: t.string,
  eventName: t.string,
  gameType: t.string,
  isMajor: t.boolean,
  noRounds: t.number,
  status: golfStatus,
  tournamentId: t.string,
  tournamentName: t.string,
  year: t.number,
  match: optional(matches),
});
export type GolfMatchplayLeaderboard = t.TypeOf<typeof golfMatchPlayLeaderboard>;

export type GolfLeaderboard = t.TypeOf<typeof golfLeaderboard>;
